/**
 * Shared form controls for Signup / Login / Forgot Password — one look for
 * inputs, buttons, labels, and field errors across the auth pages.
 *
 * Visual contract (login mockup): light gray page, white card, ~8–12px
 * corners on fields/buttons, circular remember-me, solid black primary.
 */
import React from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export const AuthInput = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { error?: boolean }
>(({ className, type, error, ...props }, ref) => (
  <input
    type={type}
    className={cn(
      'flex h-11 w-full rounded-lg border bg-white px-3 py-2 text-sm text-gray-900 transition-colors',
      'placeholder:text-gray-400',
      'focus-visible:outline-none focus-visible:border-gray-900',
      'disabled:cursor-not-allowed disabled:opacity-50',
      error ? 'border-red-500' : 'border-gray-200',
      className,
    )}
    ref={ref}
    {...props}
  />
));
AuthInput.displayName = 'AuthInput';

interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  variant?: 'default' | 'outline' | 'ghost';
}

export const AuthButton = React.forwardRef<HTMLButtonElement, AuthButtonProps>(
  ({ className, children, loading, variant = 'default', disabled, ...props }, ref) => {
    const variants = {
      default: 'bg-black text-white hover:bg-gray-900',
      outline: 'border border-gray-200 bg-white text-gray-900 hover:bg-gray-50',
      ghost: 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
    };
    return (
      <button
        className={cn(
          'inline-flex h-11 items-center justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          variants[variant],
          className,
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {children}
      </button>
    );
  },
);
AuthButton.displayName = 'AuthButton';

export const AuthLabel = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean }
>(({ className, children, required, ...props }, ref) => (
  <label ref={ref} className={cn('text-sm font-medium text-gray-900', className)} {...props}>
    {children}
    {required && (
      <span className="ml-0.5 text-red-500" aria-hidden="true">
        *
      </span>
    )}
  </label>
));
AuthLabel.displayName = 'AuthLabel';

interface AuthCheckboxProps {
  id?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export function AuthCheckbox({
  id,
  checked,
  onCheckedChange,
  disabled,
  className,
  error,
}: AuthCheckboxProps): JSX.Element {
  return (
    <button
      type="button"
      id={id}
      role="checkbox"
      aria-checked={checked}
      aria-invalid={error || undefined}
      disabled={disabled}
      onClick={() => onCheckedChange?.(!checked)}
      className={cn(
        'h-4 w-4 shrink-0 rounded-full border transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        error ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300',
        checked && !error && 'border-black bg-black',
        checked && error && 'border-red-500 bg-red-500',
        className,
      )}
    >
      {checked && <Check className="mx-auto h-3 w-3 text-white" strokeWidth={3} />}
    </button>
  );
}

export function FieldError({ id, message }: { id?: string; message?: string | null }): JSX.Element | null {
  if (!message) return null;
  return (
    <p id={id} className="text-sm text-red-600" role="alert">
      {message}
    </p>
  );
}

/** Horizontal rule with a centered “or” label between password and social login. */
export function AuthDivider({ label = 'or' }: { label?: string }): JSX.Element {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-200" />
      </div>
      <div className="relative flex justify-center text-sm">
        <span className="bg-white px-4 text-gray-500">{label}</span>
      </div>
    </div>
  );
}

interface AuthGoogleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
}

const GOOGLE_MARK = (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

/** Outlined Google CTA used under the “or” divider on login / signup. */
export function AuthGoogleButton({
  label = 'Continue with Google',
  className,
  ...props
}: AuthGoogleButtonProps): JSX.Element {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-11 w-full items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {GOOGLE_MARK}
      {label}
    </button>
  );
}
